// VendTrade: create a new trade listing. Auth required.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, DIRECTIONS } from "./shared";
import ImageUploader from "@/components/ImageUploader";
import { Image as ImageIcon, X } from "lucide-react";

export default function VendTradeNew() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [direction, setDirection] = useState("offer");
  const [category, setCategory] = useState("products");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [city, setCity] = useState(user?.location || "");
  const [imageUrl, setImageUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 3) {
      toast.error("Give it a short, punchy title.");
      return;
    }
    if (description.trim().length < 10) {
      toast.error("Tell us a bit more — at least a sentence.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/vendtrade/listings", {
        direction, category,
        title: title.trim(),
        description: description.trim(),
        city: city.trim(),
        image_url: imageUrl || null,
      });
      toast.success("Listed on VendTrade");
      nav(`/vendtrade/listings/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-6 py-14" data-testid="vendtrade-new-page">
      <div className="overline" style={{ color: "var(--terracotta)" }}>VendTrade · New listing</div>
      <h1 className="font-serif text-5xl mt-3">Post a trade.</h1>
      <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>
        Tell the community what you can offer &mdash; or what you&rsquo;re
        looking to receive. We&rsquo;ll match you with people whose trade
        goals fit yours.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-8">
        <div>
          <Label className="text-xs uppercase tracking-widest">I want to…</Label>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DIRECTIONS.map((d) => {
              const active = direction === d.value;
              const Icon = d.icon;
              return (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => setDirection(d.value)}
                  data-testid={`vendtrade-new-direction-${d.value}`}
                  className="text-left rounded-lg border p-4 transition"
                  style={{
                    borderColor: active ? "var(--terracotta)" : "rgba(0,0,0,0.15)",
                    background: active ? "rgba(196,79,54,0.05)" : "#fff",
                  }}
                >
                  <div className="flex items-center gap-2 font-medium">
                    <Icon size={16} /> {d.label}
                  </div>
                  <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
                    {d.hint}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest">Category</Label>
          <div className="mt-3 flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const active = category === c.value;
              const Icon = c.icon;
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCategory(c.value)}
                  data-testid={`vendtrade-new-category-${c.value}`}
                  className="text-sm px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition"
                  style={{
                    background: active ? c.ink : c.color,
                    color: active ? "#fff" : c.ink,
                  }}
                >
                  <Icon size={12} /> {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label htmlFor="vt-title" className="text-xs uppercase tracking-widest">Title</Label>
          <Input
            id="vt-title"
            data-testid="vendtrade-new-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={140}
            placeholder={direction === "offer"
              ? "e.g. Custom logo design in exchange for booth space"
              : "e.g. Looking for eco-friendly packaging"}
            className="mt-2"
            required
          />
        </div>

        <div>
          <Label htmlFor="vt-desc" className="text-xs uppercase tracking-widest">Details</Label>
          <Textarea
            id="vt-desc"
            data-testid="vendtrade-new-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={1200}
            rows={5}
            placeholder="What are you offering / looking for? What would make a great trade in return?"
            className="mt-2"
            required
          />
          <div className="mt-1 text-xs text-right" style={{ color: "var(--ink-soft)" }}>
            {description.length}/1200
          </div>
        </div>

        <div>
          <Label htmlFor="vt-city" className="text-xs uppercase tracking-widest">City</Label>
          <Input
            id="vt-city"
            data-testid="vendtrade-new-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="e.g. Austin, Brooklyn, San Diego"
            className="mt-2"
            required
          />
          <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
            Same-city matches get a big score boost. Regional matches still show up.
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest">Photo (optional)</Label>
          <div className="mt-2 flex items-start gap-4">
            {imageUrl ? (
              <div className="relative shrink-0" data-testid="vendtrade-new-image-preview">
                <img
                  src={imageUrl}
                  alt="Trade preview"
                  className="w-40 h-40 object-cover rounded-md border"
                  style={{ borderColor: "rgba(0,0,0,0.12)" }}
                />
                <button
                  type="button"
                  onClick={() => setImageUrl("")}
                  data-testid="vendtrade-new-image-clear"
                  className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow"
                  style={{ border: "1px solid rgba(0,0,0,0.15)" }}
                  aria-label="Remove photo"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <div
                className="w-40 h-40 rounded-md border-2 border-dashed flex items-center justify-center text-xs"
                style={{ borderColor: "rgba(0,0,0,0.12)", color: "var(--ink-soft)" }}
              >
                <ImageIcon size={22} />
              </div>
            )}
            <div className="flex-1">
              <ImageUploader
                onUploaded={setImageUrl}
                label={imageUrl ? "Replace photo" : "Add a photo"}
                testId="vendtrade-new-image"
              />
              <p className="mt-2 text-xs" style={{ color: "var(--ink-soft)" }}>
                Show what you have (or a great reference of what you&rsquo;re after).
                JPG/PNG/WEBP up to 5 MB.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4">
          <Button
            type="submit"
            data-testid="vendtrade-new-submit"
            disabled={busy}
            className="btn-primary py-6 px-8 rounded-full"
          >
            {busy ? "Posting…" : "Post to VendTrade"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => nav("/vendtrade")}
            data-testid="vendtrade-new-cancel"
            className="rounded-full"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
